/** Keyboard shortcuts that work anywhere in the app (requirements §3.9, §5.2). */

export const HistoryShortcut = { Undo: 'undo', Redo: 'redo' } as const;
export type HistoryShortcut = (typeof HistoryShortcut)[keyof typeof HistoryShortcut];

/** The parts of a KeyboardEvent the shortcut resolvers read. */
export interface KeyEventLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly target: EventTarget | null;
}

const NON_TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image',
]);

/** True when typing goes into the element: the browser's own text undo must keep working there. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest('[contenteditable]:not([contenteditable="false"])') !== null) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  return target instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(target.type);
}

/**
 * Ctrl/Cmd+Z undoes, Ctrl/Cmd+Shift+Z and Ctrl+Y redo. Returns null for any other key and while
 * focus is inside a text field.
 */
export function resolveHistoryShortcut(event: KeyEventLike): HistoryShortcut | null {
  if (event.altKey || !(event.ctrlKey || event.metaKey)) return null;
  if (isEditableTarget(event.target)) return null;
  const key = event.key.toLowerCase();
  if (key === 'z') return event.shiftKey ? HistoryShortcut.Redo : HistoryShortcut.Undo;
  if (key === 'y' && !event.shiftKey) return HistoryShortcut.Redo;
  return null;
}

/** Global date and view shortcuts (2026-10b §10.2, requirements §5.2). */
export const GlobalShortcut = {
  PreviousPeriod: 'previousPeriod',
  NextPeriod: 'nextPeriod',
  Today: 'today',
  DayView: 'dayView',
  WeekView: 'weekView',
  MonthView: 'monthView',
} as const;
export type GlobalShortcut = (typeof GlobalShortcut)[keyof typeof GlobalShortcut];

/** The parts of a KeyboardEvent the global shortcut resolver reads. */
export interface GlobalKeyEventLike {
  /** Physical key (`KeyboardEvent.code`): independent of the keyboard layout and the input method. */
  readonly code: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  /** True while an input-method composition is in progress. */
  readonly isComposing?: boolean;
  /** Reads a modifier state; `AltGraph` is how layouts with an AltGr key report it. */
  readonly getModifierState?: (key: string) => boolean;
  readonly target: EventTarget | null;
}

const PLAIN_SHORTCUTS: Readonly<Record<string, GlobalShortcut>> = {
  BracketLeft: GlobalShortcut.PreviousPeriod,
  BracketRight: GlobalShortcut.NextPeriod,
  KeyT: GlobalShortcut.Today,
  KeyD: GlobalShortcut.DayView,
  KeyW: GlobalShortcut.WeekView,
  KeyM: GlobalShortcut.MonthView,
};

/** Ctrl+Alt+1 / 3 / 4 equal D / W / M and do not depend on the input method's letter handling. */
const CHORD_SHORTCUTS: Readonly<Record<string, GlobalShortcut>> = {
  Digit1: GlobalShortcut.DayView,
  Numpad1: GlobalShortcut.DayView,
  Digit3: GlobalShortcut.WeekView,
  Numpad3: GlobalShortcut.WeekView,
  Digit4: GlobalShortcut.MonthView,
  Numpad4: GlobalShortcut.MonthView,
};

/**
 * The global shortcut a key press stands for, or null. Matches `code`, ignores Shift, and never
 * resolves while focus is in an editable element, while a composition is in progress, or with
 * Meta (and Ctrl or Alt alone) held. Whether the page state allows shortcuts (open pickers,
 * menus, dialogs, a drag) is decided by the caller.
 */
export function resolveGlobalShortcut(event: GlobalKeyEventLike): GlobalShortcut | null {
  // `key` Process / `keyCode` 229 is deliberately not a stop condition: with focus outside a text
  // field an input method in Chinese mode still delivers keys in that shape, and the shortcuts
  // must work there (2026-10b §10.2). Typing into a field is excluded by the editable-target test,
  // and an active composition by isComposing.
  if (event.isComposing === true || event.metaKey || isEditableTarget(event.target)) return null;
  if (event.ctrlKey && event.altKey) {
    // Windows reports AltGr as Ctrl+Alt: with the AltGraph state set the press types a character.
    if (event.getModifierState?.('AltGraph') === true) return null;
    return CHORD_SHORTCUTS[event.code] ?? null;
  }
  if (event.ctrlKey || event.altKey) return null;
  return PLAIN_SHORTCUTS[event.code] ?? null;
}
